import { useCallback, useEffect, useMemo } from 'react';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { useSimpleSearchJQL } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import type {
	ChildrenQueryData,
	ExpandedItems,
} from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { useHierarchyCache } from '@atlassian/jira-business-list-handlers/src/controllers/hierarchy-cache/index.tsx';
import { useUpdateIssueParent } from '@atlassian/jira-business-list-handlers/src/controllers/update-issue-parent/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	DRAG_DROP_UNKNOWN_FIELD_ID,
	FIELD_TYPE_MAP,
} from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useFlagService, type FlagConfiguration } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsLinkingEnabled } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { useOverview } from '@atlassian/jira-router-resources-business-overview-details/src/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { OnItemDrop } from '@atlassian/timeline-table/common/types/callbacks';
import type { Item } from '@atlassian/timeline-table/common/types/item';
import { useResource } from '@atlassian/react-resource-router';
import { fieldIds } from '../../common/constants.tsx';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map.tsx';
import { useIssuesMap } from '../../common/utils/use-issues-map.tsx';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMTimelineViewItemsNode,
} from '../../services/get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../../services/get-timeline-view-issues/gql.tsx';
import { useTimelineViewIssues as useTimelineViewIssuesService } from '../../services/get-timeline-view-issues/index.tsx';
import type {
	UpdateInCache,
	UpdateItemsInCache,
	RankIssueArg,
	TimelineViewIssuesReturn,
} from '../../services/get-timeline-view-issues/types.tsx';
import { addChildToParentTimeline } from '../../services/get-timeline-view-issues/utils.tsx';
import { useCreateItemAnchor } from '../create-item-anchor/index.tsx';
import messages from './messages.tsx';
import type {
	TimelineIssuesReturn,
	OnItemDropRankIssue,
	IssuesMapHash,
	IssueFieldHash,
	IssueTypeHash,
	DisplayNameHash,
} from './types.tsx';
import { getReparentAction } from './utils/index.tsx';
import { toTimelineData } from './utils/to-timeline-data.tsx';

const SUBTASK_LEVEL = -1;

export const LOADING_ITEMS_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.loadingItemsErrorTitle,
	description: messages.loadingItemsErrorDescription,
};

const RANK_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.rankingErrorTitle,
	description: messages.rankingErrorMessage,
};

// it can't be called from multiple places with the same query key and filter, otherwise it clears Apollo cache
// @ts-expect-error - TS2741 - Property 'expandedItems' is missing in type '{}' but required in type '{ childrenQueryData?: ChildrenQueryData<TimelineViewIssuesReturn> | undefined; expandedItems: Partial<Record<string, boolean>>; }'.
export const useTimelineViewIssues = ({
	childrenQueryData = {},
	expandedItems,
}: {
	childrenQueryData?: ChildrenQueryData<TimelineViewIssuesReturn>;
	expandedItems: ExpandedItems;
} = {}): TimelineIssuesReturn => {
	const { hierarchyLevelsWithCache } = useHierarchyCache({
		view: 'timeline',
	});

	const isLinkingEnabled = useIsLinkingEnabled();
	const [{ issuesMap }] = useIssuesMap();
	const [{ issueParentMap }] = useIssueParentMap();
	const { showFlag } = useFlagService();
	const [{ createItemAnchor }] = useCreateItemAnchor();
	const { quickFiltersJql, hierarchyParent, isFiltering, jqlContext } = useFilteredViewContextInput(
		{
			view: 'timeline',
		},
	);
	const { isSearching } = useSimpleSearchJQL();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: overview } = useOverview();
	const isOverview = overview != null && Array.isArray(overview.projects);

	const issueFields = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const projectContext = useResource(projectContextResource);

	const onComplete = useCallback(() => {
		if (isSearching) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'timelineSimpleSearchQuery succeeded');
		}
	}, [createAnalyticsEvent, isSearching]);

	let projectKey: string | undefined;
	// useProject is not available in the overview
	if (!isOverview) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { key } = useProject();
		projectKey = key;
	}

	const {
		data: timelineViewIssuesData,
		loadNextPage,
		loading: issuesLoading,
		isLoadingNextPage,
		updateInCache: updateInCacheTopLevel,
		rankIssue: rankIssueTopLevel,
		updateItemsInCache: updateItemsInCacheTopLevel,
		error,
	} = useTimelineViewIssuesService({
		input: {
			jqlContext,
			fieldIds,
			quickFiltersJql,
			hierarchyParent,
			hierarchyLevel: hierarchyLevelsWithCache[0]?.level,
			...(projectKey ? { projectIdOrKey: projectKey } : {}),
		},
		onComplete,
	});

	const { updateIssueParent: reparentIssue, unlinkParentIssue } = useUpdateIssueParent<
		TimelineViewIssuesReturn,
		JWMTimelineViewItemsNode,
		JWMTimelineViewItems
	>({
		childrenQueryData,
		fieldIds,
		issueParentMap,
		issuesMap,
		cacheQuery: TIMELINE_VIEW_ITEMS_QUERY,
		addChildToParent: addChildToParentTimeline<JWMTimelineViewItemsNode>,
		view: 'timeline',
		packageName: 'jiraBusinessTimeline',
		...(projectKey ? { projectKey } : {}),
	});

	useEffect(() => {
		if (error) {
			showFlag(LOADING_ITEMS_ERROR_FLAG);
		}
	}, [showFlag, error]);

	const data = toTimelineData({
		timelineViewIssuesData,
		childrenQueryData,
		expandedItems,
		createItemAnchor,
		issuesMap,
		issueParentMap,
		isLinkingEnabled,
		isFiltering,
		isOverview,
	});

	const updateInCache: UpdateInCache = useCallback(
		(params) => {
			const updatedIssueParentIssueId = issueParentMap.get(params.id);

			// if the updated issue is parent level (no parent found), we just update the parent
			if (updatedIssueParentIssueId === undefined) {
				updateInCacheTopLevel(params);
				return;
			}

			// otherwise if the updated issue is a children issue
			// get the correct callback from the specific children query
			if (childrenQueryData[updatedIssueParentIssueId]) {
				childrenQueryData[updatedIssueParentIssueId].updateInCache(params);
			}
		},
		[childrenQueryData, issueParentMap, updateInCacheTopLevel],
	);

	/*
        Using this method you can only update many issues from the top level or
        many children of the same parent.
    */
	const updateItemsInCache: UpdateItemsInCache = useCallback(
		(params) => {
			const firstIssue = params[0];
			const updatedIssueParentIssueId = issueParentMap.get(firstIssue.id);

			// if one of the issues is parent level (no parent found)
			if (updatedIssueParentIssueId === undefined) {
				updateItemsInCacheTopLevel(params);
				return;
			}

			// otherwise if the updated issue is a children issue
			// get the correct callback from the specific children query
			if (childrenQueryData[updatedIssueParentIssueId]) {
				childrenQueryData[updatedIssueParentIssueId].updateItemsInCache(params);
			}
		},
		[childrenQueryData, issueParentMap, updateItemsInCacheTopLevel],
	);

	const rankIssue: OnItemDropRankIssue = useCallback(
		(id, { rankRequest }, event, skipUpdateCache) => {
			if (!rankRequest) {
				fireTrackAnalytics(event, 'rankIssue invalidRequest');
				return;
			}

			const updatedIssueParentIssueId = issueParentMap.get(id);
			// @ts-expect-error - TS2339 - Property 'rankBeforeId' does not exist on type 'RankRequest'.
			const relativeIssueId = rankRequest.rankAfterId || rankRequest.rankBeforeId;
			const relativeIssue = issuesMap.get(relativeIssueId);
			if (!relativeIssue) {
				fireTrackAnalytics(event, 'rankIssue invalidRequest', {
					relativeIssue: relativeIssueId,
				});
				showFlag(RANK_ERROR_FLAG);
				return;
			}

			const params: RankIssueArg = {
				issueId: id,
				relativeIssue,
				skipUpdateCache,
				// @ts-expect-error - TS2339 - Property 'rankBeforeId' does not exist on type 'RankRequest'.
				rankCall: rankRequest.rankBeforeId ? 'rankBefore' : 'rankAfter',
			};

			if (updatedIssueParentIssueId === undefined) {
				rankIssueTopLevel(params);
				return;
			}

			// get the correct callback from the specific children query
			if (childrenQueryData[updatedIssueParentIssueId]) {
				childrenQueryData[updatedIssueParentIssueId].rankIssue(params);
			}
		},
		[issueParentMap, issuesMap, childrenQueryData, showFlag, rankIssueTopLevel],
	);

	const updateIssueParent: OnItemDrop = useCallback(
		async (id, payload, event) => {
			const { parentId, rankRequest, level, isMeatballsAction } = payload;
			const parentIssue = parentId ? issuesMap.get(parentId) : undefined;
			const parentIdOld = issueParentMap.get(id);
			// There is a race condition with state/cache updates where users are able to drag a subtask to the root level and drop it if they're quick enough.
			// This ensures we block the update as its not a valid action.
			if (level === SUBTASK_LEVEL && !parentId) {
				return;
			}

			if (!parentId) {
				await unlinkParentIssue({ issueKey: id, rankRequest });
			} else {
				if (parentIdOld === parentId) {
					return;
				}
				if (!parentIssue) {
					fireTrackAnalytics(event, 'reparent invalidRequest', {
						parentId,
					});
					return;
				}
				await reparentIssue({
					issueKey: id,
					parentIssue,
					rankRequest,
					...(fg('jwm_timeline_meatball_reparent_menu') ? { isMeatballsAction } : {}),
				});
			}

			fg('one_event_rules_them_all_fg') &&
				getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(DRAG_DROP_UNKNOWN_FIELD_ID, {
					analytics: createAnalyticsEvent({}),
					attributes: {
						fieldKey: PARENT_TYPE,
						fieldType: FIELD_TYPE_MAP[PARENT_TYPE],
						oldValId: parentIdOld,
						newValId: parentId,
						isDragEditing: true,
					},
				});
			fireUIAnalytics(createAnalyticsEvent({}), 'timelineDragAndDrop reparent', {
				action: getReparentAction(parentId, parentIdOld),
				isRanking: !!rankRequest,
				hierarchyLevel: level,
				parentHierarchyLevel: parentIssue?.issueType?.hierarchyLevel,
				...(fg('jwm_timeline_meatball_reparent_menu') ? { isMeatballsAction } : {}),
			});

			// Re-rank the issue if a rank request is provided during the reparenting process
			if (rankRequest) {
				rankIssue(id, payload, event, true);
			}
		},
		[createAnalyticsEvent, issueParentMap, issuesMap, rankIssue, reparentIssue, unlinkParentIssue],
	);

	const flattenItems = useCallback(
		(items: Item[] | undefined): Item[] => {
			if (!items) return [];

			return items.flatMap((item) => {
				const isExpanded = expandedItems[toIssueId(item.id)];
				const children = isExpanded ? flattenItems(item.childItems) : [];
				return [item, ...children] as const;
			});
		},
		[expandedItems],
	);

	const flattenedItems = useMemo(() => flattenItems(data?.items), [data?.items, flattenItems]);

	const issueIds = useMemo(() => {
		return Array.from(data.issuesMap.keys());
	}, [data]);

	const issuesHash = useMemo(() => {
		const issuesParentMap = data.issueParentMap;

		const issueHashReturn: IssuesMapHash = {};

		data.issuesMap.forEach((value, key) => {
			issueHashReturn[key] = {
				id: value.id,
				issueId: value.issueId,
				key: value.key,
				rank: value.rank,
				issueType: value.issueType,
				hasChildren: value.hasChildren,
				status: value.status,
				summary: { value: value.summary },
				startDate: { value: value.startDate },
				dueDate: { value: value.dueDate },
				assignee: value.assignee?.accountId,
				issueTypeId: value.issueType.id,
				// Unused attributes, to be updated when timeline is harmonized.
				// Used for type comformation
				flagged: false,
				color: { value: 'GREY' },
			};

			const parentId = { value: issuesParentMap.has(key) ? issuesParentMap.get(key) : undefined };

			issueHashReturn[key] = {
				...issueHashReturn[key],
				parentId,
			};
		});
		return issueHashReturn;
	}, [data]);

	const userDisplayNameHash = useMemo(() => {
		const displayNameHash: DisplayNameHash = {};

		data.issuesMap.forEach((value) => {
			if (value.assignee) {
				displayNameHash[value.assignee.accountId] = value.assignee.displayName;
			}
		});
		return displayNameHash;
	}, [data]);

	const issueTypesHash = useMemo(() => {
		const issueTypes: IssueTypeHash = {};

		data.issuesMap.forEach((value) => {
			const type = value.issueType;
			if (!issueTypes[type.id]) {
				issueTypes[type.id] = {
					name: type.name,
					iconUrl: type.iconUrl,
				};
			}
		});

		return issueTypes;
	}, [data]);

	const issueFieldsHash = useMemo(() => {
		const {
			data: { fields },
		} = issueFields;
		// Pre-populate with default fields
		const fieldsHash: IssueFieldHash = { issuekey: 'Key', parent: 'Parent' };

		fields.forEach((field) => {
			fieldsHash[field.id] = field.name;
		});
		return fieldsHash;
	}, [issueFields]);

	const projectName = useMemo(
		() => projectContext?.data?.projectName || 'Untitled project',
		[projectContext],
	);

	return {
		loading: issuesLoading,
		error,
		data,
		loadNextPage,
		isLoadingNextPage,
		updateInCache,
		rankIssue,
		updateItemsInCache,
		isLinkingEnabled,
		updateIssueParent,
		flattenedItems,
		csvProps: {
			issueIds,
			issuesHash,
			userDisplayNameHash,
			issueTypesHash,
			issueFields: issueFieldsHash,
			projectName,
			startDateCustomFieldId: 'Start date',
			hasInferredStartDueDateFields: false,
		},
	};
};
